import React, { useCallback, useState, useRef, useEffect } from 'react'
import gsap from 'gsap'
import { SplitText as gsapSplitText } from 'gsap/SplitText'
import PropTypes from 'prop-types'
import FontFaceObserver from 'fontfaceobserver'

// Hooks
import { useScrolledAnimation } from '../useScrolledAnimation'

// Styles
import { SplitTextOuter, SplitTextWrapper } from './index.style'
import { font } from '../../../styles/vars/font.style'

gsap.registerPlugin(gsapSplitText)

const AnimateSplitText = ({
  children,
  id,
  type,
  delay,
  useInView,
  isInView,
  syncWithScroll,
  debug,
}) => {
  const [transition, setTransition] = useState({ current: null })
  const spansRef = useRef(null),
    isVisible = useRef(false)

  useScrolledAnimation({
    id: id,
    transition: transition,
    sync: syncWithScroll,
    delay: delay,
    useInView: useInView,
    isInView: isInView,
    isVisible: isVisible,
    debug: debug,
  })

  const createTransition = useCallback(() => {
    if (!spansRef.current || isVisible.current) return

    const spansIn = gsap.fromTo(
      spansRef.current,
      {
        opacity: 0,
        y: '30',
      },
      {
        paused: true,
        ease: 'power2.out',
        duration: 0.5,
        opacity: 1,
        y: 0,
        stagger: 0.1,
      }
    )

    setTransition({ current: spansIn })
    if (debug) console.log(spansIn)
  }, [spansRef, isVisible, debug])

  const content = useRef()
  const splitContent = useRef()
  const childrenArray = children.props ? children.props.children : children
  const ariaLabel =
    typeof children === 'object'
      ? React.Children.toArray(childrenArray)
          .filter(item => (typeof item === 'object' ? '' : item))
          .join('')
      : children

  const build = useCallback(() => {
    splitContent.current = new gsapSplitText(content.current, {
      type: type,
      span: true,
    })

    splitContent.current[type].forEach((item, itemIndex) => {
      item.classList.add('split__mask')
      item.setAttribute('aria-hidden', true)
    })

    spansRef.current = splitContent.current.elements[0].childNodes
    createTransition()
  }, [type, createTransition])

  const update = useCallback(() => {
    splitContent.current.revert()
    build()
  }, [build])

  const textRef = useCallback(
    node => {
      if (node !== null && !node.classList.contains('split')) {
        const fontA = new FontFaceObserver(font.heading.family),
          fontB = new FontFaceObserver(font.headingAlt.family)

        Promise.all([fontA.load(null, 8000), fontB.load(null, 8000)]).then(
          () => {
            content.current = node
            build()

            setTimeout(() => {
              content.current.classList.add('split')
            }, 10)

            window.addEventListener('resize', update)
          },
          () => {
            console.log('Fonts not available')
          }
        )
      }
    },
    [build, update]
  )

  useEffect(() => {
    return () => {
      window.removeEventListener('resize', update)
    }
  }, [update])

  if (!useInView) {
    return (
      <SplitTextOuter>
        <SplitTextWrapper ref={textRef} aria-label={ariaLabel}>
          {children}
        </SplitTextWrapper>
      </SplitTextOuter>
    )
  }

  return (
    <SplitTextOuter data-scroll data-scroll-id={id}>
      <SplitTextWrapper ref={textRef} aria-label={ariaLabel}>
        {children}
      </SplitTextWrapper>
    </SplitTextOuter>
  )
}

AnimateSplitText.propTypes = {
  children: PropTypes.node.isRequired,
  id: PropTypes.string.isRequired,
  delay: PropTypes.number,
  type: PropTypes.oneOf(['lines', 'words', 'chars', 'words,chars']),
  useInView: PropTypes.bool,
  isInView: PropTypes.bool,
  syncWithScroll: PropTypes.bool,
  debug: PropTypes.bool,
}

AnimateSplitText.defaultProps = {
  type: 'lines',
  delay: 0.2,
  useInView: true,
  isInView: false,
  syncWithScroll: false,
  debug: false,
}

export default AnimateSplitText
